import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TokenStorageService } from './token-storage.service';
import { map } from 'rxjs/operators';
import { Backend } from '../_models/backend';
import { RouterConstants } from '../_models/router-constants';
import { ApiConstants } from '../_models/api-constants';
import LoginRequest = Backend.LoginRequest;
import BoardLoginRequest = Backend.BoardLoginRequest;
import JwtResponse = Backend.JwtResponse;
import Role = Backend.Role;

const AUTH_API = ApiConstants.API_AUTH_ADDRESS_BASE;
const AUTH_ENTRY_POINT = AUTH_API + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_USER;
const AUTH_ENTRY_POINT_ADMIN = AUTH_API + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_ADMIN;
const AUTH_ENTRY_POINT_SCHOOL_ADMIN = AUTH_API + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_SCHOOL_ADMIN;
const AUTH_ENTRY_POINT_BOARD = AUTH_API + ApiConstants.API_AUTH_ADDRESS_REQUEST_TOKEN_BOARD;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private currentUserSubject$: BehaviorSubject<JwtResponse | null> = new BehaviorSubject<JwtResponse | null>(null);
  public currentUser$: Observable<JwtResponse | null> = new Observable<JwtResponse | null>();

  isVirtualBoard = false;

  constructor(private http: HttpClient, private tokenStorage: TokenStorageService) {
    const user = this.tokenStorage.getUser();

    if (user != null) {
      this.currentUserSubject$ = new BehaviorSubject<JwtResponse | null>(user);
    }
    this.currentUser$ = this.currentUserSubject$.asObservable();
  }

  public get currentUserValue(): JwtResponse | null {
    return this.currentUserSubject$.value;
  }

  login(loginRequest: LoginRequest, url: string): Observable<JwtResponse> {
    let apiUrl = AUTH_ENTRY_POINT;

    if (url.includes(RouterConstants.SCHOOL_ADMIN)) {
      apiUrl = AUTH_ENTRY_POINT_SCHOOL_ADMIN;
    } else if (url.includes(RouterConstants.ADMIN)) {
      apiUrl = AUTH_ENTRY_POINT_ADMIN;
    }

    return this.http.post<JwtResponse>(apiUrl, loginRequest)
      .pipe(map(user => {
        this.saveUser(user);
        this.tokenStorage.saveOauthLogin(loginRequest.oauth);
        return user;
      }));
  }

  registerBoard(boardLoginRequest: BoardLoginRequest): Observable<JwtResponse> {
    return this.http.post<JwtResponse>(AUTH_ENTRY_POINT_BOARD, boardLoginRequest)
      .pipe(map(user => {
        this.saveBoard(user);
        return user;
      }));
  }

  openVirtualBoard(jwtResponse: JwtResponse): void {
    this.tokenStorage.saveVirtualBoard(jwtResponse);
    const boardWindow = window.open(RouterConstants.BOARD, '_blank');
    if (boardWindow != null) { // clear, ugyanis örökölné ebből a window-ból
      boardWindow.sessionStorage.clear();
    }
  }

  logout(close = false): void {
    // remove user from session storage to log user out
    this.clearUser();

    if (close) {
      window.close();
    } else {
      location.reload();
    }
  }

  clearUser(): void {
    // remove user from session storage to log user out
    this.tokenStorage.clearSessionStorage();
    this.tokenStorage.clearLocalStorage();

    this.currentUserSubject$.next(null);
  }

  checkUser(roles: string[] | undefined): boolean {
    const currentUser = this.currentUserValue;
    const intersect = currentUser?.roles?.filter(x => roles?.includes(x));

    return intersect !== null && intersect !== undefined && intersect.length > 0;
  }

  isOathLogin(): boolean {
    return this.tokenStorage.getOauthLogin();
  }

  isTeacher(): boolean {
    return this.hasRole('ROLE_TEACHER');
  }

  isMentor(): boolean {
    return this.hasRole('ROLE_MENTOR');
  }

  isAdmin(): boolean {
    return this.hasRole('ROLE_ADMIN');
  }

  isSchoolAdmin(): boolean {
    return this.hasRole('ROLE_SCHOOLADMIN');
  }

  hasRole(role: Role): boolean {
    const currentUser = this.currentUserSubject$.value;
    if (currentUser != null && currentUser.roles != null) {
      return currentUser.roles.indexOf(role) >= 0;
    }

    return false;
  }

  getUserName(): string {
    return this.currentUserValue != null && this.currentUserValue.name != null ? this.currentUserValue.name : '';
  }

  getUsername(): string {
    return this.currentUserValue != null && this.currentUserValue.username != null ? this.currentUserValue.username : '';
  }

  private saveUser(user: JwtResponse): void {
    // store user details and jwt accessToken in session storage to keep user logged in between page refreshes
    this.tokenStorage.saveUser(user);
    this.currentUserSubject$.next(user);
  }

  private saveBoard(user: JwtResponse): void {
    // store user details and jwt accessToken in local storage to keep user logged in between window close
    this.tokenStorage.saveBoard(user);
    this.currentUserSubject$.next(user);
  }
}
